import { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * Collapse-then-expand primitive: a widget mounts collapsed, holds for `holdMs`, then auto-expands —
 * the entrance beat from Guri's context demo, for widgets that have a panel but no row list to tick
 * (web search, goal/outline drafts, answered questions). Source widgets with a row list use
 * `useSourceAutoplay` instead, which layers row-ticking on top of the same collapse→expand.
 *
 * When `enabled` is false the widget is expanded from the start (existing presentational behaviour).
 * Reduced-motion callers skip straight to expanded.
 */
export function useAutoExpand(enabled: boolean, holdMs = 1600): boolean {
  const reducedMotion = useReducedMotion();
  const live = enabled && !reducedMotion;

  const [expanded, setExpanded] = useState(!live);

  useEffect(() => {
    if (!live) {
      setExpanded(true);
      return;
    }
    setExpanded(false);
    const timer = window.setTimeout(() => setExpanded(true), holdMs);
    return () => window.clearTimeout(timer);
  }, [live, holdMs]);

  return expanded;
}
